import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { KeyRound, Mail } from "lucide-react";
import { API_BASE_URL } from "./api";
import { Button, Field, SectionHeader, SurfaceCard } from "./components/designSystem";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const hasDebugResetDetails = Boolean(result?.token && result?.resetLink);

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setResult(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email })
      });
      const data = await res.json();
      setResult(data);
    } catch (err) {
      setResult({ error: "Network error" });
    }
    setLoading(false);
  };

  const handleGoToReset = () => {
    navigate("/reset-password");
  };

  return (
    <div className="min-h-[calc(100vh-140px)] bg-[#f3efe7] px-6 py-12 dark:bg-[linear-gradient(180deg,_#020617_0%,_#111827_45%,_#0f172a_100%)] lg:px-10">
      <div className="mx-auto grid max-w-5xl gap-6 xl:grid-cols-[0.9fr_1.1fr]">
        <SurfaceCard className="overflow-hidden bg-[linear-gradient(135deg,_#0f172a,_#111827_58%,_#1d4ed8)] text-white dark:border-white/10">
          <SectionHeader
            eyebrow="Recovery"
            title="Request a password reset"
            description="Use the account email to send a reset link. Development environments can still expose the raw token and link for internal testing."
          />
          <div className="mt-8 rounded-3xl border border-white/10 bg-white/10 p-4 text-sm leading-7 text-slate-200">
            When SMTP is configured, MekEdge emails the reset link directly. When it is not, the API can still return the raw reset details for controlled development use.
          </div>
        </SurfaceCard>

        <SurfaceCard className="bg-white dark:bg-gray-900">
          <SectionHeader
            eyebrow="Forgot password"
            title="Generate a reset link"
            description="Enter the email associated with the account you want to recover."
          />
          <form onSubmit={handleSubmit} className="mt-6 space-y-5">
            <Field
              label="Email"
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
            />
            <Button type="submit" variant="brand" disabled={loading}>
              <Mail className="h-4 w-4" />
              {loading ? "Sending..." : "Send Reset Link"}
            </Button>
          </form>
          {result ? (
            <div className="mt-5 rounded-3xl border border-slate-200 bg-slate-50 p-5 text-sm dark:border-gray-700 dark:bg-gray-800">
              {result.success ? (
                <>
                  <p className="font-semibold text-emerald-600">{result.message || "Reset instructions sent."}</p>
                  {hasDebugResetDetails ? (
                    <>
                      <p className="mt-3 break-all text-xs text-slate-600 dark:text-slate-300">Token: <span className="font-mono">{result.token}</span></p>
                      <p className="mt-2 break-all text-xs text-slate-600 dark:text-slate-300">Reset Link: <a href={result.resetLink} className="text-sky-600 underline">{result.resetLink}</a></p>
                    </>
                  ) : (
                    <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">Check the inbox for this account email. The link expires in one hour.</p>
                  )}
                  <div className="mt-4 flex flex-wrap gap-3">
                    {hasDebugResetDetails ? (
                      <Button type="button" variant="brand" onClick={handleGoToReset}>
                        <KeyRound className="h-4 w-4" />
                        Go to Reset Password
                      </Button>
                    ) : null}
                    <Button type="button" variant="secondary" onClick={() => navigate("/login")}>Back to Login</Button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-rose-600">{result.error || result.message}</p>
                  <Button type="button" variant="secondary" className="mt-4" onClick={() => navigate("/login")}>Back to Login</Button>
                </>
              )}
            </div>
          ) : null}
          <div className="mt-5 text-sm text-slate-500 dark:text-slate-400">
            Prefer the combined auth page? <Link to="/login" className="font-semibold text-sky-600">Return to login</Link>.
          </div>
        </SurfaceCard>
      </div>
    </div>
  );
}
